import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import moment from 'moment-timezone';

import { dateFormat } from '../shared/constants/CommonConstants';

export const formatDate = (dateString: string, formatString: string, locale: string): string => {
    const date = moment(dateString);
    return date.locale(locale).format(formatString);
};

export const formatRelativeTime = (dateString: string, locale: string): string => {
    const date = moment(dateString);
    return date.locale(locale).fromNow();
};

export const getMonthName = (monthIndex: number, locale: string): string => {
    return moment().locale(locale).month(monthIndex).format('MMMM');
};

export const getWeekdayName = (weekdayIndex: number, locale: string): string => {
    return moment().locale(locale).weekday(weekdayIndex).format('dddd');
};

export const getTimezoneName = (locale: string): string => {
    return moment.tz(moment.tz.guess()).locale(locale).zoneName();
};

export const setLocale = (locale: string): void => {
    moment.locale(locale);
};

export const getLocale = (): string => {
    return moment.locale();
};

export const getTimeZone = () => {
    const currentMoment = moment();
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const timeZoneValue = currentMoment.tz(timeZone).format('Z'); // Returns the time zone value (e.g., "+5:30")
    return timeZoneValue;
};

export const getCurrentISODate = (): string => {
    return new Date().toISOString();
};

export const formatAsRelativeTime = (timestamp: string, t: TFunction) => {
    const dayjsObj = dayjs(timestamp);
    if (!dayjsObj.isValid()) {
        return '';
    }

    const now = dayjs();
    const currentWeekDay = now.day();
    const targetWeekDay = dayjsObj.day();
    const diffInDays = now.diff(dayjsObj, 'day');

    if (diffInDays === 0 && currentWeekDay === targetWeekDay) {
        return dayjsObj.format('H:mm');
    }
    if (diffInDays < 7 && (targetWeekDay + 1) % 7 === currentWeekDay) {
        return `${t('yesterday')} ${dayjsObj.format('H:mm')}`;
    }
    if (diffInDays < 7) {
        return `${t(`weekday_${targetWeekDay}`)} ${dayjsObj.format('H:mm')}`;
    }
    if (dayjsObj.year() === now.year()) {
        return dayjsObj.format('MM-DD H:mm');
    }
    return dayjsObj.format('YYYY-MM-DD H:mm');
};

/**
 * return translation key for relative time group: Today, Yesterday, Previous 7 days, Previous 30 days, This year, Older
 */
export const getRelativeTimeGroupKey = (timestamp: string) => {
    const dayjsObj = dayjs(timestamp);
    if (!dayjsObj.isValid()) {
        return '';
    }

    const now = dayjs();
    const currentWeekDay = now.day();
    const targetWeekDay = dayjsObj.day();
    const diffInDays = now.diff(dayjsObj, 'day');

    if (diffInDays === 0 && currentWeekDay === targetWeekDay) {
        return 'today';
    }
    if (diffInDays < 7 && (targetWeekDay + 1) % 7 === currentWeekDay) {
        return 'yesterday';
    }
    if (diffInDays < 7) {
        return 'previous_7_days';
    }
    if (diffInDays < 30) {
        return 'previous_30_days';
    }
    if (dayjsObj.year() === now.year()) {
        return 'this_year';
    }
    return 'older';
};

export const formatMonth = (date?: Date | string) =>
    moment(date).format(dateFormat).replace('..', '.');
